<script setup lang="ts">
import { ECheckpointActions } from '@/store/checkpointStore/CheckpointStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { DropZoneData } from '@/types/components/DropZoneData'
import { ERdrChannel, hasAvailableRdrChannel, RdrChannelKey } from '@/types/enum/RdrChannelEnum'
import type { FFDragEvent } from '@/types/Firefox/DragEvent'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { AssociatedDeviceModel, DeviceModel } from '@/types/models/DeviceModel'
import { BaseDialog, StackLayout, TypographyItem } from '@rfc/rfc-components-library'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import DeviceAvailableRdrChannels from '../../../../../components/DeviceComponents/components/DeviceAvailableRdrChannels.vue'

const store = useStore()
type Props = {
    dropZoneData?: DropZoneData
    checkpoint?: CheckpointModel
    hasDevices?: boolean
}
const props = defineProps<Props>()
type Emits = {
    (e: 'close'): void
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const dropZoneRef = ref<HTMLDivElement>()

const localDevice = ref<AssociatedDeviceModel | DeviceModel>()
watch(
    () => props.dropZoneData?.device,
    newValue => (newValue ? (localDevice.value = newValue) : undefined)
)

const selectedRdrChannel = ref<ERdrChannel | undefined>()
const availableRdrChannels = computed(() =>
    localDevice.value && props.checkpoint?.associates
        ? hasAvailableRdrChannel(localDevice.value, props.checkpoint?.associates)
        : undefined
)

watch(
    () => props.dropZoneData?.position,
    newPosition => {
        if (!newPosition) return
        if (props.dropZoneData?.checkpointId === props.checkpoint?.id) {
            hovering.value = ''
            return emit('close')
        }
        const rect = dropZoneRef.value?.getBoundingClientRect()

        if (
            rect &&
            newPosition.x > rect.left &&
            newPosition.x < rect.right &&
            newPosition.y > rect.top &&
            newPosition.y < rect.bottom
        )
            return (open.value = true)

        emit('close')
    }
)

const handlePointerEnter = (event: DragEvent) => {
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    hovering.value = 'dropzone-success'
}
const handlePointerLeave = (event: FFDragEvent) => {
    const rect = dropZoneRef.value?.getBoundingClientRect()
    if (!rect) return
    if (
        (event._ffix_cx ?? event.x) > rect?.left &&
        (event._ffix_cx ?? event.x) < rect.right &&
        (event._ffix_cy ?? event.y) > rect.top &&
        (event._ffix_cy ?? event.y) < rect.bottom
    )
        return
    hovering.value = ''
}

const open = ref(false)
const handleConfirm = () => {
    store
        ?.dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.ASSOCIATE_DEVICE}`, {
            device_id: localDevice.value?.device_id ?? localDevice.value?.id,
            checkpoint_id: props.checkpoint?.id,
            event_id: props.checkpoint?.event_id,
            config: selectedRdrChannel.value
                ? { [RdrChannelKey]: selectedRdrChannel.value }
                : undefined,
            force: true
        })
        .then(() => {
            emit('refresh')
            handleClose()
        })
}
const handleClose = () => {
    open.value = false
    /* localDevice.value = undefined */
    hovering.value = ''
    emit('close')
}
const hovering = ref('')

defineExpose({ ref: dropZoneRef })
</script>

<template>
    <div
        ref="dropZoneRef"
        @dragover.prevent
        @dragenter="handlePointerEnter"
        @dragleave="handlePointerLeave"
        @dragend="handlePointerLeave"
        :class="`flex color-text checkpoint-dropdown w-full ${hasDevices ? 'flex-auto' : ''}`"
        v-bind="$attrs">
        <slot :class="`${!!dropZoneData?.device ? 'dropzone-card' : ''} ${hovering}`" />
    </div>
    <BaseDialog
        :title="$t('checkpoint.actions.reassignDeviceTitle')"
        :open="open"
        @confirm="handleConfirm"
        @close="handleClose"
        class="event-checkpoint-component modal-selector-half-width">
        <template #content>
            <StackLayout direction="column" :gap="8">
                <TypographyItem
                    :label="
                        checkpoint
                            ? $t('checkpoint.actions.reassignDevice', {
                                  device: localDevice?.name,
                                  checkpoint: checkpoint?.name
                              })
                            : $t('checkpoint.actions.unassignDevice')
                    " />
                <DeviceAvailableRdrChannels
                    :device="localDevice"
                    :selectedRdrChannel="selectedRdrChannel"
                    @selectChannel="selectedRdrChannel = $event" />
            </StackLayout>
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.dropzone-card {
    & :deep(.card-header) {
        pointer-events: none;
    }
    & :deep(.card-content) {
        pointer-events: none;
    }
    & :deep(.card-footer) {
        pointer-events: none;
    }
}

.checkpoint-dropdown {
    flex-grow: 1;
    & :deep(.checkpoint-card) {
        &.dropzone-success {
            border-color: rgba(var(--v-theme-success), 0.5);
            box-shadow: 1px 1px 7px rgba(var(--v-theme-success));
        }
    }
}
</style>
